import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authGet, authPost, authPut, authDelete } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction
} from '../components/ui/alert-dialog';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { TrendUp, TrendDown, Plus, Trash, PencilSimple, FunnelSimple, X, CaretLeft, CaretRight, ArrowsClockwise } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const CURRENCY_SYMBOLS = {
  USD: '$', EUR: '€', GBP: '£', CAD: 'C$', AUD: 'A$', INR: '₹', JPY: '¥'
};

const CATEGORIES = {
  income: ['Salary', 'Part-time Job', 'Freelance', 'Allowance', 'Scholarship', 'Gift', 'Other'],
  expense: ['Food', 'Transport', 'Rent', 'Entertainment', 'Shopping', 'Subscriptions', 'Bills & Utilities', 'Other']
};

const ALL_CATEGORIES = [...new Set([...CATEGORIES.income, ...CATEGORIES.expense])];

export default function Transactions() {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  
  // Filter states
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedType, setSelectedType] = useState('all');

  const [form, setForm] = useState({
    type: 'expense',
    amount: '',
    category: '',
    date: new Date().toISOString().split('T')[0],
    note: '',
    recurring: false
  });

  const currency = user?.currency || 'USD';
  const symbol = CURRENCY_SYMBOLS[currency] || '$';

  const fetchTransactions = useCallback(async () => {
    try {
      const { data } = await authGet('/api/transactions?limit=500');
      setTransactions(data);
    } catch (e) {
      console.error('Fetch transactions error:', e);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const resetForm = () => {
    setForm({
      type: 'expense',
      amount: '',
      category: '',
      date: new Date().toISOString().split('T')[0],
      note: '',
      recurring: false
    });
    setEditingTransaction(null);
  };

  const openAddDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (transaction) => {
    setEditingTransaction(transaction);
    setForm({
      type: transaction.type,
      amount: transaction.amount.toString(),
      category: transaction.category,
      date: transaction.date,
      note: transaction.note || '',
      recurring: transaction.recurring || false
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Explicit validation
    if (!form.amount || parseFloat(form.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (!form.category) {
      toast.error('Please select a category');
      return;
    }
    if (!form.date) {
      toast.error('Please select a date');
      return;
    }
    
    setSubmitting(true);

    try {
      const payload = {
        type: form.type,
        amount: parseFloat(form.amount),
        category: form.category,
        date: form.date,
        note: form.note || '',
        recurring: form.recurring
      };

      console.log('Submitting transaction:', payload);

      if (editingTransaction) {
        await authPut(`/api/transactions/${editingTransaction.id}`, payload);
        toast.success('Transaction updated');
      } else {
        await authPost('/api/transactions', payload);
        toast.success('Transaction added');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchTransactions();
    } catch (e) {
      console.error('Save transaction error:', e);
      const msg = e.response?.data?.detail || e.message || 'Failed to save transaction';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    }
    setSubmitting(false);
  };

  const handleDelete = async (id) => {
    if (!id) {
      console.error('Delete called with no ID');
      toast.error('Cannot delete: missing transaction ID');
      return;
    }
    
    // Store original transactions for rollback
    const originalTransactions = [...transactions];
    
    // Optimistic update - remove from UI immediately
    setTransactions(prev => prev.filter(t => t.id !== id));
    setDeleteTarget(null);
    
    try {
      console.log(`Deleting transaction: ${id}`);
      const response = await authDelete(`/api/transactions/${id}`);
      console.log('Delete response:', response.data);
      
      toast.success('Transaction deleted');
      // Refresh to ensure sync with server
      fetchTransactions();
    } catch (e) {
      console.error('Delete transaction error:', e.response?.status, e.response?.data || e.message);
      // Revert on error
      setTransactions(originalTransactions);
      
      const errorMessage = e.response?.data?.detail || e.message || 'Failed to delete transaction';
      toast.error(`Delete failed: ${typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage)}`);
    }
  };

  const formatCurrency = (amount) => {
    return `${symbol}${(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Filter transactions
  const filteredTransactions = useMemo(() => {
    return transactions.filter(t => {
      // Filter by month
      if (selectedMonth !== 'all') {
        const transactionMonth = t.date.substring(0, 7); // YYYY-MM
        if (transactionMonth !== selectedMonth) return false;
      }
      
      // Filter by category
      if (selectedCategory !== 'all' && t.category !== selectedCategory) return false;
      
      // Filter by type
      if (selectedType !== 'all' && t.type !== selectedType) return false;
      
      return true;
    });
  }, [transactions, selectedMonth, selectedCategory, selectedType]);

  // Calculate totals for filtered transactions
  const totals = useMemo(() => {
    const income = filteredTransactions
      .filter(t => t.type === 'income')
      .reduce((sum, t) => sum + t.amount, 0);
    const expenses = filteredTransactions
      .filter(t => t.type === 'expense')
      .reduce((sum, t) => sum + t.amount, 0);
    return { income, expenses, net: income - expenses };
  }, [filteredTransactions]);

  // Get unique months from transactions
  const availableMonths = useMemo(() => {
    const months = new Set();
    transactions.forEach(t => {
      months.add(t.date.substring(0, 7));
    });
    return Array.from(months).sort().reverse();
  }, [transactions]);

  // Get unique categories from transactions
  const usedCategories = useMemo(() => {
    const cats = new Set();
    transactions.forEach(t => cats.add(t.category));
    return Array.from(cats).sort();
  }, [transactions]);

  const groupByDate = (transactions) => {
    const groups = {};
    transactions.forEach(t => {
      const date = t.date;
      if (!groups[date]) groups[date] = [];
      groups[date].push(t);
    });
    return Object.entries(groups).sort((a, b) => new Date(b[0]) - new Date(a[0]));
  };

  const formatMonthDisplay = (monthStr) => {
    if (monthStr === 'all') return 'All Time';
    const [year, month] = monthStr.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const navigateMonth = (direction) => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const newDate = new Date(year, month - 1 + direction);
    setSelectedMonth(`${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, '0')}`);
  };

  const clearFilters = () => {
    const now = new Date();
    setSelectedMonth(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
    setSelectedCategory('all');
    setSelectedType('all');
  };

  const hasActiveFilters = selectedCategory !== 'all' || selectedType !== 'all';

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[#3B82F6]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="transactions-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Transactions</h1>
          <p className="text-[#9CA3AF] mt-1">Track your income and expenses</p>
        </div>
        
        <Button 
          onClick={openAddDialog}
          className="btn-primary flex items-center gap-2" 
          data-testid="add-transaction-btn"
        >
          <Plus size={18} />
          <span className="hidden sm:inline">Add Transaction</span>
        </Button>
      </div>

      {/* Month Navigation & Filters */}
      <div className="card-default p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Month Navigation */}
          <div className="flex items-center gap-2 flex-1">
            <button
              onClick={() => navigateMonth(-1)}
              className="p-2 rounded-lg bg-[#1F2937] hover:bg-[#374151] transition-colors"
              data-testid="prev-month-btn"
            >
              <CaretLeft size={20} />
            </button>
            <div className="flex-1 text-center">
              <p className="font-semibold text-lg">{formatMonthDisplay(selectedMonth)}</p>
            </div>
            <button
              onClick={() => navigateMonth(1)}
              className="p-2 rounded-lg bg-[#1F2937] hover:bg-[#374151] transition-colors"
              data-testid="next-month-btn"
            >
              <CaretRight size={20} />
            </button>
          </div>

          {/* Category Filter */}
          <div className="flex items-center gap-2">
            <FunnelSimple size={18} className="text-[#9CA3AF]" />
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-[160px] bg-[#1F2937] border-white/10" data-testid="category-filter">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent className="bg-[#111827] border-white/10">
                <SelectItem value="all" className="text-[#F9FAFB] focus:bg-[#1F2937]">All Categories</SelectItem>
                {usedCategories.map(cat => (
                  <SelectItem key={cat} value={cat} className="text-[#F9FAFB] focus:bg-[#1F2937]">
                    {cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Type Filter */}
          <Select value={selectedType} onValueChange={setSelectedType}>
            <SelectTrigger className="w-[140px] bg-[#1F2937] border-white/10" data-testid="type-filter">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent className="bg-[#111827] border-white/10">
              <SelectItem value="all" className="text-[#F9FAFB] focus:bg-[#1F2937]">All Types</SelectItem>
              <SelectItem value="income" className="text-[#10B981] focus:bg-[#1F2937]">Income</SelectItem>
              <SelectItem value="expense" className="text-[#EF4444] focus:bg-[#1F2937]">Expense</SelectItem>
            </SelectContent>
          </Select>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 text-sm text-[#9CA3AF] hover:text-[#F9FAFB] transition-colors"
              data-testid="clear-filters-btn"
            >
              <X size={16} />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="card-default p-4">
          <p className="text-sm text-[#9CA3AF]">Income</p>
          <p className="text-xl font-semibold text-[#10B981] tabular-nums">
            {formatCurrency(totals.income)}
          </p>
        </div>
        <div className="card-default p-4">
          <p className="text-sm text-[#9CA3AF]">Expenses</p>
          <p className="text-xl font-semibold text-[#EF4444] tabular-nums">
            {formatCurrency(totals.expenses)}
          </p>
        </div>
        <div className="card-default p-4">
          <p className="text-sm text-[#9CA3AF]">Net</p>
          <p className={`text-xl font-semibold tabular-nums ${totals.net >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
            {totals.net >= 0 ? '+' : ''}{formatCurrency(totals.net)}
          </p>
        </div>
      </div>

      {/* Transaction List */}
      <div className="card-default" data-testid="transactions-list">
        {filteredTransactions.length > 0 ? (
          <div className="divide-y divide-white/5">
            {groupByDate(filteredTransactions).map(([date, items]) => (
              <div key={date}>
                <div className="px-5 py-3 bg-[#0B0F1A]/50 flex items-center justify-between">
                  <p className="text-sm text-[#9CA3AF] font-medium">
                    {new Date(date).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                  </p>
                  <p className="text-xs text-[#9CA3AF]">
                    {items.length} transaction{items.length !== 1 ? 's' : ''}
                  </p>
                </div>
                {items.map(t => (
                  <div 
                    key={t.id} 
                    className="flex items-center justify-between p-4 hover:bg-white/[0.02] transition-colors group"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        t.type === 'income' ? 'bg-[#10B981]/20' : 'bg-[#EF4444]/20'
                      }`}>
                        {t.type === 'income' ? (
                          <TrendUp size={20} className="text-[#10B981]" />
                        ) : (
                          <TrendDown size={20} className="text-[#EF4444]" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <p className="font-medium truncate">{t.category}</p>
                          {t.recurring && (
                            <span 
                              className="flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium bg-[#8B5CF6]/20 text-[#8B5CF6] rounded-full flex-shrink-0"
                              data-testid={`recurring-badge-${t.id}`}
                            >
                              <ArrowsClockwise size={10} />
                              Recurring
                            </span>
                          )}
                        </div>
                        {t.note && <p className="text-xs text-[#9CA3AF] truncate">{t.note}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-4">
                      <p className={`font-semibold tabular-nums ${
                        t.type === 'income' ? 'text-[#10B981]' : 'text-[#EF4444]'
                      }`}>
                        {t.type === 'income' ? '+' : '-'}{formatCurrency(t.amount)}
                      </p>
                      <div className="flex items-center gap-1 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => openEditDialog(t)}
                          className="text-[#9CA3AF] hover:text-[#3B82F6] transition-colors p-1.5"
                          data-testid={`edit-transaction-${t.id}`}
                        >
                          <PencilSimple size={18} />
                        </button>
                        <button
                          onClick={() => setDeleteTarget(t.id)}
                          className="text-[#9CA3AF] hover:text-[#EF4444] transition-colors p-1.5"
                          data-testid={`delete-transaction-${t.id}`}
                        >
                          <Trash size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-[#9CA3AF]">
              {transactions.length === 0 
                ? 'No transactions yet' 
                : 'No transactions match your filters'
              }
            </p>
            <p className="text-sm text-[#9CA3AF] mt-1">
              {transactions.length === 0 
                ? 'Add your first transaction to start tracking'
                : 'Try adjusting your filters'
              }
            </p>
          </div>
        )}
      </div>

      {/* Add/Edit Transaction Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }} modal={true}>
        <DialogContent className="bg-[#111827] border-white/10 max-w-md" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-[#F9FAFB]">
              {editingTransaction ? 'Edit Transaction' : 'Add Transaction'}
            </DialogTitle>
            <DialogDescription className="text-sm text-[#6B7280]">
              {editingTransaction ? 'Update the transaction details below.' : 'Fill in the details to add a new transaction.'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-5 mt-4">
            <Tabs value={form.type} onValueChange={(v) => setForm({ ...form, type: v, category: '' })}>
              <TabsList className="grid grid-cols-2 bg-[#1F2937]">
                <TabsTrigger 
                  value="expense" 
                  className="data-[state=active]:bg-[#EF4444]/20 data-[state=active]:text-[#EF4444]"
                  data-testid="expense-tab"
                >
                  <TrendDown size={16} className="mr-2" />
                  Expense
                </TabsTrigger>
                <TabsTrigger 
                  value="income" 
                  className="data-[state=active]:bg-[#10B981]/20 data-[state=active]:text-[#10B981]"
                  data-testid="income-tab"
                >
                  <TrendUp size={16} className="mr-2" />
                  Income
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <div className="space-y-2">
              <Label className="text-[#F9FAFB]">Amount</Label>
              <div className="flex items-center gap-2">
                <span className="text-lg font-medium text-[#9CA3AF] w-8">{symbol}</span>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={form.amount}
                  onChange={(e) => setForm(prev => ({ ...prev, amount: e.target.value }))}
                  placeholder="0.00"
                  className="input-default flex-1"
                  required
                  data-testid="transaction-amount"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[#F9FAFB]">Category</Label>
              <Select 
                value={form.category} 
                onValueChange={(v) => setForm(prev => ({ ...prev, category: v }))}
              >
                <SelectTrigger className="input-default" data-testid="transaction-category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="bg-[#111827] border-white/10">
                  {CATEGORIES[form.type].map(cat => (
                    <SelectItem key={cat} value={cat} className="text-[#F9FAFB] focus:bg-[#1F2937]">
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-[#F9FAFB]">Date</Label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => setForm(prev => ({ ...prev, date: e.target.value }))}
                className="input-default w-full"
                required
                data-testid="transaction-date"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-[#F9FAFB]">Note (optional)</Label>
              <Input
                value={form.note}
                onChange={(e) => setForm(prev => ({ ...prev, note: e.target.value }))}
                placeholder="Add a note..."
                className="input-default w-full"
                data-testid="transaction-note"
              />
            </div>

            <div className="flex items-center justify-between">
              <Label className="text-[#F9FAFB]">Recurring transaction</Label>
              <Switch
                checked={form.recurring}
                onCheckedChange={(c) => setForm(prev => ({ ...prev, recurring: c }))}
                data-testid="transaction-recurring"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button 
                type="button" 
                onClick={() => { setDialogOpen(false); resetForm(); }}
                className="btn-secondary flex-1"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={submitting || !form.amount || parseFloat(form.amount) <= 0 || !form.category || !form.date}
                className={form.type === 'income' ? 'flex-1 bg-[#10B981] hover:bg-[#059669]' : 'flex-1 bg-[#EF4444] hover:bg-[#DC2626]'}
                data-testid="save-transaction-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingTransaction ? 'Update' : 'Save')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent className="bg-[#111827] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#F9FAFB]">Delete transaction?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#9CA3AF]">
              This will permanently remove this transaction. Your dashboard totals and budgets will update automatically.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              className="bg-[#1F2937] border-white/10 text-[#F9FAFB] hover:bg-[#374151]"
              data-testid="cancel-delete-btn"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-[#EF4444] text-white hover:bg-[#DC2626]"
              onClick={() => handleDelete(deleteTarget)}
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
